/**
 * "View Results" on an estimate or claim that waits on the office: what our
 * team found, for whom, and what to do — then "Update Smilepass on progress".
 *
 * Read top to bottom:
 *   1. The request — what was asked and when.
 *   2. Patient | Member — the breakdown tab's own table (PlanSummary's
 *      PeopleTable): the two names, "Show details" for the rest.
 *   3. Each item waiting on the office, the heart of it: its status, the facts
 *      that identify it, what our team found (their note) and what to do next.
 *   4. The office's update: a note and a file per item, sent back to our team.
 *
 * Today's waiting outcomes on an item:
 *   - Resubmit to Carrier: the carrier needs it sent again; the office does so,
 *     then tells us so we follow it up.
 *   - Correspondence sent to patient/member: the carrier answered the member,
 *     not the office; the office gets the letter from them and tells us.
 *   - Details Needed by Smilepass (on an item): our team needs something first.
 *
 * Everything shown comes from the row already loaded. Updating us is the same
 * reply the office has always sent: the note (and a file per item, if any)
 * goes on each waiting item and the request goes back to our team to follow
 * up (POST .../resubmit/ — the parent sends it).
 */
import React, { useState } from "react";
import { Button, Input, Modal, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import { PeopleTable, memberFields, patientFields } from "../TreatmentPlans/component/Breakdown/sheet/PlanSummary";
import CollapsibleFilePreview from "../TreatmentPlans/component/shared/CollapsibleFilePreview";
import { resultDocumentUrl } from "../TreatmentPlans/component/shared/estimateClaimsApi";
import { formatDay, outcomeChipStyle, outcomeLabel } from "./outcomes";
import { requestTypeMeta } from "./requestTypes";
import {
  RESUBMIT_ACCEPT, RESUBMIT_MAX_BYTES, handBackEntry, itemTitle, policyAskOf, waitingItems,
} from "./rowContext";

const ORDINALS = { primary: "Primary", secondary: "Secondary", tertiary: "Tertiary", other: "Other" };

function capitalize(s) {
  const t = String(s || "");
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

// The row's policy in the shape the breakdown's people table reads.
function peoplePolicy(policy) {
  const p = policy || {};
  return Object.assign({}, p, {
    insurance_name_display: p.carrier_name,
    insurance_type_display: ORDINALS[p.insurance_type] || p.insurance_type,
    relation_to_member_display: capitalize(p.relation_to_member),
  });
}

function memberName(policy) {
  const name = [policy && policy.first_name, policy && policy.last_name].filter(Boolean).join(" ").trim();
  return name || "the patient or member";
}

/** What the office does next, by why the item waits on them. */
export function nextStep(outcome, { carrier, member, noun }) {
  if (outcome === "needs_resubmission") {
    return "The carrier needs this " + noun + " sent again, with the changes above. Once you've resubmitted it"
      + (carrier ? " to " + carrier : "") + ", update Smilepass so we can follow it up.";
  }
  if (outcome === "correspondence_sent") {
    return (carrier || "The carrier") + " sent its answer to " + member + " rather than to your office. "
      + "Ask them for the letter. Once you have it — or have heard from them — update Smilepass, and attach "
      + "the letter if you can.";
  }
  if (outcome === "missing_info") {
    return "Our team needs something before we can go on — see the note above. Send it with your update.";
  }
  return "Update Smilepass once you've dealt with this.";
}

function attachLabel(outcome, noun) {
  if (outcome === "needs_resubmission") return "Attach the resubmitted " + noun;
  if (outcome === "correspondence_sent") return "Attach the carrier's letter";
  return "Attach a file";
}

// The facts that identify an item, each under its own label — only the ones
// we have.
function itemFacts(item, entry, carrier) {
  const facts = [];
  if (item.provider_name) facts.push({ label: "Provider", value: item.provider_name });
  const codes = (item.procedures || []).map(function (p) {
    return p.code + (p.tooth ? " (tooth " + p.tooth + ")" : "");
  });
  if (codes.length) facts.push({ label: codes.length === 1 ? "Procedure" : "Procedures", value: codes.join(", ") });
  if (item.cda_itrans_number) facts.push({ label: "CDA / iTrans #", value: item.cda_itrans_number });
  if (entry && entry.contacted_on) {
    facts.push({ label: "Carrier contacted", value: (carrier ? carrier + " · " : "") + formatDay(entry.contacted_on) });
  }
  return facts;
}

export default function RequestResultsModal({ row, busy, onCancel, onSubmit, onViewHistory }) {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState({});   // { [item id]: antd fileList }
  const meta = requestTypeMeta(row && row.request_type);
  const noun = meta.submissionNoun || "estimate";
  const policy = (row && row.policy) || {};
  const carrier = policy.carrier_name || "";
  const all = (row && row.requested_estimates) || [];
  const items = waitingItems(row);
  const policyAsk = policyAskOf(row);
  const people = peoplePolicy(policy);

  function submit() {
    const picked = {};
    Object.keys(files).forEach(function (id) {
      const list = files[id] || [];
      if (list.length && list[0].originFileObj) picked[id] = list[0].originFileObj;
    });
    onSubmit({ note: note.trim(), files: picked });
  }

  return (
    <Modal
      open
      title={meta.noun + " results"}
      width={760}
      onCancel={onCancel}
      destroyOnClose
      className="ir-rr-modal"
      footer={[
        <Button key="close" onClick={onCancel} disabled={busy}>Close</Button>,
        <Button key="history" onClick={onViewHistory} disabled={busy}>View full history</Button>,
        <Button key="update" type="primary" loading={busy} onClick={submit}>
          Update Smilepass on progress
        </Button>,
      ]}
    >
      <div className="ir-rr">
        <div className="ir-rr-request" data-testid="results-request">
          <span className="ir-rr-request-type">{meta.rowLabel}</span>
          {row && row.created_at ? (
            <span className="ir-rr-request-when">Requested {formatDay(String(row.created_at).slice(0, 10))}</span>
          ) : null}
        </div>

        <PeopleTable
          id="ir-rr-people"
          patientRows={patientFields(row && row.patient, people)}
          memberRows={memberFields(people)}
        />

        {policyAsk ? (
          <div className="ir-rr-item">
            <div className="ir-rr-item-head">
              <span className="ir-rr-item-title">What our team asked</span>
            </div>
            <div className="ir-rr-found"><div className="ir-rr-found-text">{policyAsk}</div></div>
          </div>
        ) : null}

        {items.map(function (item) {
          const entry = handBackEntry(item);
          const facts = itemFacts(item, entry, carrier);
          return (
            <section key={item.id} className="ir-rr-item" data-testid={"result-item-" + item.id}>
              <div className="ir-rr-item-head">
                <span className="ir-rr-item-title">{itemTitle(item, meta, all.indexOf(item), all.length)}</span>
                <span className="ir-rr-status">
                  <span className="ir-rr-status-label">Latest status update</span>
                  <span className="ir-rr-chip" style={outcomeChipStyle(item.outcome)}>{outcomeLabel(item.outcome)}</span>
                </span>
              </div>

              {facts.length ? (
                <dl className="ir-rr-facts">
                  {facts.map(function (f) {
                    return (
                      <div key={f.label} className="ir-rr-fact">
                        <dt>{f.label}</dt>
                        <dd>{f.value}</dd>
                      </div>
                    );
                  })}
                </dl>
              ) : null}

              <div className="ir-rr-found">
                <div className="ir-rr-eyebrow">What our team found</div>
                <div className="ir-rr-found-text">{(entry && entry.note) || "—"}</div>
                {entry && entry.has_document ? (
                  <div className="ir-rr-doc">
                    <CollapsibleFilePreview
                      url={resultDocumentUrl(entry.id)}
                      fileName={entry.document_name}
                      label="carrier-response"
                      height={420}
                      showText="Show the carrier's response"
                      hideText="Hide the carrier's response"
                    />
                  </div>
                ) : null}
              </div>

              <div className="ir-rr-next">
                <div className="ir-rr-eyebrow">What to do next</div>
                <div className="ir-rr-next-text">
                  {nextStep(item.outcome, { carrier: carrier, member: memberName(policy), noun: noun })}
                </div>
              </div>
            </section>
          );
        })}

        <section className="ir-rr-update">
          <div className="ir-rr-update-title">Update Smilepass on progress</div>
          <div className="ir-rr-update-hint">
            Tell us what you've done. This sends it back to our team to follow up.
          </div>
          <Input.TextArea
            rows={3}
            value={note}
            maxLength={2000}
            onChange={function (e) { setNote(e.target.value); }}
            placeholder="Optional — e.g. resubmitted on Oct 16 with the corrected tooth number, or: the patient is bringing the letter in"
            aria-label="Your update for Smilepass"
          />
          {items.length ? (
            <div className="ir-rr-attach">
              {items.map(function (item) {
                const list = files[item.id] || [];
                const which = items.length > 1 ? " — " + itemTitle(item, meta, all.indexOf(item), all.length) : "";
                return (
                  <Upload
                    key={item.id}
                    accept={RESUBMIT_ACCEPT}
                    maxCount={1}
                    fileList={list}
                    beforeUpload={function (file) {
                      if (file.size > RESUBMIT_MAX_BYTES) {
                        message.error("That file is larger than 20MB. Try a smaller scan or photo.");
                        return Upload.LIST_IGNORE;
                      }
                      return false;   // sent with the update, not on its own
                    }}
                    onChange={function (info) {
                      const next = info.fileList.slice(-1);
                      setFiles(function (prev) { return Object.assign({}, prev, { [item.id]: next }); });
                    }}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      {(list.length ? "Replace the file" : attachLabel(item.outcome, noun)) + which + " (optional)"}
                    </Button>
                  </Upload>
                );
              })}
            </div>
          ) : null}
        </section>
      </div>
    </Modal>
  );
}
